import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { makeStyles, Typography, IconButton, Tooltip, Button, TextField } from "@material-ui/core";
import { getListRowId } from "./utils/listRowHandles";
import {
  Close as CloseIcon,
  PlayArrow,
  Pause,
  SkipNext,
  Replay,
  Send,
  Speed,
} from "@material-ui/icons";

const ACCENT = "#9a00ed";
const GREEN = "#22c55e";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const GRAY = "#94a3b8";

const useStyles = makeStyles(() => ({
  root: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 340,
    backgroundColor: "#fff",
    borderTop: `2px solid ${ACCENT}30`,
    boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
    zIndex: 25,
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
  },
  hidden: {
    transform: "translateY(360px)",
    pointerEvents: "none",
  },
  visible: {
    transform: "translateY(0)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "6px 14px",
    borderBottom: "1px solid #eee",
    gap: 8,
    flexShrink: 0,
    backgroundColor: "#fafafa",
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1a1a1a",
    flex: 1,
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  controlBtn: {
    padding: 4,
  },
  speedChip: {
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: 4,
    backgroundColor: `${ACCENT}15`,
    color: ACCENT,
    cursor: "pointer",
    userSelect: "none",
  },
  body: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
    minHeight: 0,
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #eee",
    minWidth: 0,
  },
  chatMessages: {
    flex: 1,
    overflowY: "auto",
    padding: "10px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  chatInputArea: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderTop: "1px solid #eee",
    backgroundColor: "#fafafa",
  },
  chatInput: {
    flex: 1,
    "& .MuiInputBase-input": {
      fontSize: 12,
      padding: "8px 12px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 20,
    },
  },
  sendBtn: {
    padding: 6,
    color: ACCENT,
  },
  msgBot: {
    alignSelf: "flex-start",
    backgroundColor: "#e8f5e9",
    borderRadius: "12px 12px 12px 2px",
    padding: "6px 12px",
    fontSize: 12,
    maxWidth: "80%",
    wordBreak: "break-word",
    lineHeight: 1.5,
  },
  msgUser: {
    alignSelf: "flex-end",
    backgroundColor: "#e3f2fd",
    borderRadius: "12px 12px 2px 12px",
    padding: "6px 12px",
    fontSize: 12,
    maxWidth: "80%",
    wordBreak: "break-word",
    lineHeight: 1.5,
  },
  msgSystem: {
    alignSelf: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: "4px 12px",
    fontSize: 11,
    color: "#888",
    textAlign: "center",
    fontStyle: "italic",
  },
  optionBtns: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
    alignSelf: "flex-start",
  },
  optionBtn: {
    fontSize: 11,
    padding: "4px 12px",
    borderRadius: 16,
    border: `1px solid ${ACCENT}`,
    color: ACCENT,
    backgroundColor: "#fff",
    cursor: "pointer",
    transition: "all 0.15s",
    "&:hover": {
      backgroundColor: `${ACCENT}10`,
    },
  },
  varsPanel: {
    width: 240,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  },
  varsPanelHeader: {
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
    fontSize: 12,
    fontWeight: 700,
    color: "#555",
    backgroundColor: "#fafafa",
  },
  varsList: {
    flex: 1,
    overflowY: "auto",
    padding: 8,
  },
  varRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "3px 6px",
    fontSize: 11,
    borderRadius: 4,
    gap: 6,
  },
  varKey: {
    color: "#7c3aed",
    fontWeight: 600,
    fontFamily: "'Fira Code', monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  varValue: {
    color: "#333",
    fontFamily: "'Fira Code', monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 120,
    textAlign: "right",
  },
  statusBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 14px",
    borderTop: "1px solid #eee",
    backgroundColor: "#fafafa",
    fontSize: 11,
    color: "#888",
    flexShrink: 0,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
  },
  nodeLabel: {
    fontWeight: 600,
    color: "#333",
  },
}));

// ========================================
// Simulation Engine (client-side)
// ========================================
class SimEngine {
  constructor(nodes, edges) {
    this.nodes = nodes || [];
    this.edges = edges || [];
    this.variables = {};
    this.currentNodeId = null;
    this.executedIds = new Set();
    this.stepCount = 0;
    this.maxSteps = 500;
  }

  start() {
    const startNode = this.nodes.find((n) => n.type === "start");
    if (!startNode) return null;
    this.currentNodeId = startNode.id;
    this.executedIds.add(startNode.id);
    this.stepCount = 0;
    return startNode;
  }

  reset(nodes, edges) {
    this.nodes = nodes || this.nodes;
    this.edges = edges || this.edges;
    this.variables = {};
    this.currentNodeId = null;
    this.executedIds = new Set();
    this.stepCount = 0;
  }

  getNode(id) {
    return this.nodes.find((n) => n.id === id);
  }

  getCurrentNode() {
    return this.currentNodeId ? this.getNode(this.currentNodeId) : null;
  }

  getNextNodeId(handleId) {
    const edge = this.edges.find(
      (e) => e.source === this.currentNodeId && e.sourceHandle === handleId
    );
    return edge ? edge.target : null;
  }

  advance(handleId) {
    if (this.stepCount >= this.maxSteps) return null;
    const nextId = this.getNextNodeId(handleId);
    if (!nextId) return null;
    this.currentNodeId = nextId;
    this.executedIds.add(nextId);
    this.stepCount++;
    return this.getNode(nextId);
  }

  resolveVars(text) {
    if (!text || typeof text !== "string") return text || "";
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      return this.variables[key] !== undefined ? String(this.variables[key]) : `{{${key}}}`;
    });
  }

  setVar(key, value) {
    if (key) this.variables[key] = value;
  }

  evaluateCondition(node) {
    const { key, condition, value } = node.data || {};
    const actual = this.variables[key];
    const expected = value;
    const ops = { 1: "==", 2: ">=", 3: "<=", 4: "<", 5: ">" };
    const op = ops[condition] || "==";
    const a = isNaN(Number(actual)) ? actual : Number(actual);
    const b = isNaN(Number(expected)) ? expected : Number(expected);
    switch (op) {
      case "==": return String(a) === String(b);
      case ">=": return a >= b;
      case "<=": return a <= b;
      case ">": return a > b;
      case "<": return a < b;
      default: return false;
    }
  }
}

// ========================================
// NODE_LABELS — nomes legiveis
// ========================================
const NODE_LABELS = {
  start: "Inicio", message: "Mensagem", condition: "Condicao",
  menu: "Menu", button: "Botoes", buttonPix: "PIX", list: "Lista",
  input: "Captura", interval: "Intervalo", wait: "Aguardar",
  ticket: "Fila", tag: "Tag", removeTag: "Remover Tag",
  attendant: "Atendente", switchFlow: "Trocar Fluxo",
  httpRequest: "HTTP", variable: "Variavel", setAdvanced: "Set",
  code: "Codigo", openai: "OpenAI", gemini: "Gemini",
  aiAgent: "Agente IA", img: "Imagem", audio: "Audio",
  video: "Video", singleBlock: "Conteudo", randomizer: "Aleatorio",
  filter: "Filtro", ifAdvanced: "IF Avancado", switch: "Switch",
  merge: "Merge", loop: "Loop", splitBatch: "Lotes",
  stopError: "Parar", sendTemplate: "Template", sendLocation: "Localizacao",
  sendContact: "Contato", sendSticker: "Sticker", sendReaction: "Reacao",
  emailSmtp: "Email", textClassifier: "Classificador", stickyNote: "Nota",
  dateTime: "Data/Hora", text: "Texto", regex: "Regex",
  campaignPro: "Campanha", kanbanLane: "Kanban Lane", closeTicket: "Fechar Ticket",
  webhookTrigger: "Webhook", scheduleTrigger: "Agendamento",
};

function getNodeLabel(node) {
  if (!node) return "?";
  return node.data?.labelOverride || node.data?.label || NODE_LABELS[node.type] || node.type;
}

// ========================================
// ExecutionPreview Component
// ========================================
export default function ExecutionPreview({
  open,
  onClose,
  nodes,
  edges,
  setNodes,
  fitView,
}) {
  const classes = useStyles();
  const [engine] = useState(() => new SimEngine([], []));
  const [messages, setMessages] = useState([]);
  const [simStatus, setSimStatus] = useState("idle"); // idle | running | waiting | finished | error
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [variables, setVariables] = useState({});
  const [autoPlay, setAutoPlay] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [inputValue, setInputValue] = useState("");
  const [waitingFor, setWaitingFor] = useState(null); // { type, options?, node }
  const chatRef = useRef(null);
  const autoPlayRef = useRef(false);
  const openRef = useRef(false);

  // Sync refs
  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);
  useEffect(() => { openRef.current = open; }, [open]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Speed cycle
  const SPEEDS = [2000, 1000, 500];
  const cycleSpeed = useCallback(() => {
    setSpeed((prev) => {
      const idx = SPEEDS.indexOf(prev);
      return SPEEDS[(idx + 1) % SPEEDS.length];
    });
  }, []);

  // ─── Highlight nodes on canvas ───
  const updateCanvasHighlights = useCallback((engineRef) => {
    if (!openRef.current) return;
    setNodes((nds) =>
      nds.map((n) => {
        let status = null;
        if (n.id === engineRef.currentNodeId) status = "current";
        else if (engineRef.executedIds.has(n.id)) status = "executed";
        return {
          ...n,
          data: { ...n.data, _simStatus: status },
        };
      })
    );
  }, [setNodes]);

  // ─── Focus node on canvas ───
  const focusNode = useCallback((nodeId) => {
    const targetNode = nodes.find((n) => n.id === nodeId);
    if (targetNode && fitView) {
      fitView({ nodes: [targetNode], padding: 0.5, duration: 400, maxZoom: 1.5 });
    }
  }, [nodes, fitView]);

  // ─── Add message to chat ───
  const addMsg = useCallback((type, text, options) => {
    setMessages((prev) => [...prev, { type, text, options, ts: Date.now() }]);
  }, []);

  // ─── Process a single node ───
  const processNode = useCallback((node) => {
    if (!node) return;
    const { type, data } = node;
    const label = getNodeLabel(node);

    switch (type) {
      case "start":
        addMsg("system", "Simulacao iniciada");
        return { autoAdvance: true, handle: "a" };

      case "message": {
        const text = engine.resolveVars(data?.label || data?.text || data?.message || "");
        addMsg("bot", text || `[${label}]`);
        return { autoAdvance: true, handle: "a" };
      }

      case "singleBlock": {
        const text = engine.resolveVars(data?.label || data?.message || "");
        addMsg("bot", text || `[Conteudo]`);
        return { autoAdvance: true, handle: "a" };
      }

      case "img":
        addMsg("bot", `[Imagem: ${data?.url || data?.label || "imagem"}]`);
        return { autoAdvance: true, handle: "a" };

      case "audio":
        addMsg("bot", `[Audio: ${data?.url || data?.label || "audio"}]`);
        return { autoAdvance: true, handle: "a" };

      case "video":
        addMsg("bot", `[Video: ${data?.url || data?.label || "video"}]`);
        return { autoAdvance: true, handle: "a" };

      case "sendTemplate":
        addMsg("bot", `[Template HSM: ${data?.templateName || "template"}]`);
        return { autoAdvance: true, handle: "a" };

      case "sendLocation":
        addMsg("bot", `[Localizacao: ${data?.lat || "?"}, ${data?.lng || "?"}]`);
        return { autoAdvance: true, handle: "a" };

      case "sendContact":
        addMsg("bot", `[Contato vCard: ${data?.contactName || "contato"}]`);
        return { autoAdvance: true, handle: "a" };

      case "sendSticker":
        addMsg("bot", `[Sticker]`);
        return { autoAdvance: true, handle: "a" };

      case "sendReaction":
        addMsg("system", `Reacao: ${data?.emoji || "👍"}`);
        return { autoAdvance: true, handle: "a" };

      case "menu": {
        const msg = engine.resolveVars(data?.message || "");
        const options = (data?.arrayOption || []).map((o) => ({
          number: o.number,
          label: o.value || `Opcao ${o.number}`,
          handle: "a" + o.number,
        }));
        addMsg("bot", msg || "[Menu]");
        setWaitingFor({ type: "menu", options, node });
        return { autoAdvance: false };
      }

      case "button": {
        const msg = engine.resolveVars(data?.message || "");
        const options = (data?.arrayOption || []).map((o) => ({
          number: o.number,
          label: o.value || `Botao ${o.number}`,
          handle: "a" + o.number,
        }));
        addMsg("bot", msg || "[Botoes]");
        setWaitingFor({ type: "button", options, node });
        return { autoAdvance: false };
      }

      case "buttonPix": {
        addMsg("bot", engine.resolveVars(data?.message || "[Botao PIX]"));
        return { autoAdvance: true, handle: "a1" };
      }

      case "list": {
        const msg = engine.resolveVars(data?.message || "");
        addMsg("bot", msg || "[Lista]");
        // Lists have dynamic handles. For simulation, show simple options
        const opts = (data?.sections || []).flatMap((s) =>
          (s.rows || [])
            .map((r) => {
              const rowId = getListRowId(r);
              if (!rowId) return null;
              return {
                number: rowId,
                label: r.title || `Item ${rowId}`,
                handle: "lst_" + rowId,
              };
            })
            .filter(Boolean)
        );
        if (opts.length > 0) {
          setWaitingFor({ type: "list", options: opts, node });
          return { autoAdvance: false };
        }
        return { autoAdvance: true, handle: "a" };
      }

      case "input": {
        const question = engine.resolveVars(data?.question || data?.label || "");
        addMsg("bot", question || "[Aguardando entrada]");
        setWaitingFor({ type: "input", node, varName: data?.variableName || data?.variable || "input" });
        return { autoAdvance: false };
      }

      case "condition": {
        const result = engine.evaluateCondition(node);
        addMsg("system", `Condicao "${label}": ${result ? "SIM" : "NAO"}`);
        return { autoAdvance: true, handle: result ? "a" : "b" };
      }

      case "ifAdvanced": {
        // Simplified: evaluate first condition only
        const conds = data?.conditions || [];
        let result = true;
        for (const c of conds) {
          const actual = engine.variables[c.field];
          const expected = c.value;
          const op = c.op || "==";
          if (op === "==" && String(actual) !== String(expected)) { result = false; break; }
          if (op === "!=" && String(actual) === String(expected)) { result = false; break; }
          if (op === ">" && !(Number(actual) > Number(expected))) { result = false; break; }
          if (op === "<" && !(Number(actual) < Number(expected))) { result = false; break; }
          if (op === ">=" && !(Number(actual) >= Number(expected))) { result = false; break; }
          if (op === "<=" && !(Number(actual) <= Number(expected))) { result = false; break; }
        }
        addMsg("system", `IF Avancado: ${result ? "VERDADEIRO" : "FALSO"}`);
        return { autoAdvance: true, handle: result ? "a" : "b" };
      }

      case "switch": {
        const field = engine.variables[data?.switchField] || "";
        const cases = data?.cases || [];
        let matchHandle = "default";
        for (let i = 0; i < cases.length; i++) {
          if (String(field) === String(cases[i].value)) {
            matchHandle = `case_${i}`;
            break;
          }
        }
        addMsg("system", `Switch: "${field}" → ${matchHandle}`);
        return { autoAdvance: true, handle: matchHandle };
      }

      case "filter": {
        const field = engine.variables[data?.filterField] || "";
        const op = data?.filterOp || "==";
        const val = data?.filterValue || "";
        let pass = false;
        if (op === "==") pass = String(field) === String(val);
        else if (op === "contains") pass = String(field).includes(val);
        else if (op === "startsWith") pass = String(field).startsWith(val);
        else if (op === "endsWith") pass = String(field).endsWith(val);
        else if (op === "isEmpty") pass = !field;
        else if (op === "isNotEmpty") pass = !!field;
        addMsg("system", `Filtro: ${pass ? "passou" : "bloqueado"}`);
        return { autoAdvance: true, handle: pass ? "pass" : "reject" };
      }

      case "randomizer": {
        const r = Math.random() > 0.5;
        addMsg("system", `Aleatorio: caminho ${r ? "A" : "B"}`);
        return { autoAdvance: true, handle: r ? "a" : "b" };
      }

      case "interval":
      case "wait":
        addMsg("system", `Aguardando ${data?.sec || data?.seconds || "?"} seg. (simulado)`);
        return { autoAdvance: true, handle: "a" };

      case "variable":
        engine.setVar(data?.variableName, engine.resolveVars(data?.variableValue || ""));
        setVariables({ ...engine.variables });
        addMsg("system", `Variavel: ${data?.variableName} = ${engine.variables[data?.variableName]}`);
        return { autoAdvance: true, handle: "variable-out" };

      case "setAdvanced": {
        try {
          const fields = typeof data?.fields === "string" ? JSON.parse(data.fields) : data?.fields;
          if (fields && typeof fields === "object") {
            Object.entries(fields).forEach(([k, v]) => engine.setVar(k, engine.resolveVars(String(v))));
            setVariables({ ...engine.variables });
          }
        } catch (_) {}
        addMsg("system", `Set Avancado executado`);
        return { autoAdvance: true, handle: "a" };
      }

      case "ticket":
        addMsg("system", `Transferido para fila: ${data?.queueName || data?.label || "fila"}`);
        return { autoAdvance: true, handle: "a" };

      case "tag":
        addMsg("system", `Tag adicionada: ${data?.tagName || data?.label || "tag"}`);
        return { autoAdvance: true, handle: "a" };

      case "removeTag":
        addMsg("system", `Tag removida: ${data?.tagName || data?.label || "tag"}`);
        return { autoAdvance: true, handle: "a" };

      case "attendant":
        addMsg("system", `Transferido para atendente: ${data?.userName || data?.label || "atendente"}`);
        return { autoAdvance: true, handle: "a" };

      case "switchFlow":
        addMsg("system", `Trocou para fluxo: ${data?.flowName || data?.label || "outro fluxo"}`);
        return { autoAdvance: false, terminal: true };

      case "stopError":
        addMsg("system", `ERRO: ${data?.errorMessage || "Fluxo interrompido"}`);
        return { autoAdvance: false, terminal: true };

      case "httpRequest":
        addMsg("system", `[Simulado] HTTP ${data?.method || "GET"} ${data?.url || "..."}`);
        if (data?.responseVar) {
          engine.setVar(data.responseVar, '{"simulado": true}');
          setVariables({ ...engine.variables });
        }
        return { autoAdvance: true, handle: "httpRequest-out" };

      case "openai":
      case "gemini":
      case "aiAgent":
        addMsg("bot", `[Simulado] Resposta IA (${type})`);
        if (data?.outputVar) {
          engine.setVar(data.outputVar, "[resposta simulada da IA]");
          setVariables({ ...engine.variables });
        }
        return { autoAdvance: true, handle: type === "aiAgent" ? "success" : "a" };

      case "textClassifier":
        addMsg("system", `[Simulado] Classificador: categoria 1`);
        return { autoAdvance: true, handle: "cat_0" };

      case "code":
        addMsg("system", `[Simulado] Codigo JS executado`);
        return { autoAdvance: true, handle: "a" };

      case "dateTime":
        addMsg("system", `[Simulado] Data/Hora processado`);
        return { autoAdvance: true, handle: "a" };

      case "text":
        addMsg("system", `[Simulado] Texto processado`);
        return { autoAdvance: true, handle: "a" };

      case "regex":
        addMsg("system", `[Simulado] Regex executado`);
        return { autoAdvance: true, handle: "match" };

      case "emailSmtp":
        addMsg("system", `[Simulado] Email enviado para ${data?.to || "..."}`);
        return { autoAdvance: true, handle: "sent" };

      case "merge":
        addMsg("system", `Merge: caminhos unificados`);
        return { autoAdvance: true, handle: "a" };

      case "loop":
        addMsg("system", `[Simulado] Loop: 1 iteracao`);
        return { autoAdvance: true, handle: "done" };

      case "splitBatch":
        addMsg("system", `[Simulado] Lote processado`);
        return { autoAdvance: true, handle: "done" };

      case "campaignPro":
        addMsg("system", `Campanha Pro: ${data?.action || "ação"} na lista ${data?.list?.name || "..."}`);
        return { autoAdvance: true, handle: "a" };

      case "closeTicket":
        addMsg("system", "Ticket encerrado pelo fluxo");
        return { autoAdvance: false, terminal: true };

      case "kanbanLane":
        addMsg("system", `Kanban: ${data?.classicLane?.name || data?.enterpriseLane?.name || "lane"}`);
        return { autoAdvance: true, handle: "a" };

      case "stickyNote":
        // Skip — not executable
        return { autoAdvance: true, handle: "a" };

      case "errorTrigger":
        addMsg("system", "Trigger de erro ativado");
        return { autoAdvance: true, handle: "a" };

      case "webhookTrigger":
        addMsg("system", `[Simulado] Webhook recebido: ${data?.webhookPath || "/"}`);
        if (data?.bodyVar) {
          engine.setVar(data.bodyVar, '{"simulado": true}');
          setVariables({ ...engine.variables });
        }
        return { autoAdvance: true, handle: "a" };

      case "scheduleTrigger":
        addMsg("system", "[Simulado] Agendamento disparado");
        return { autoAdvance: true, handle: "a" };

      default:
        addMsg("system", `[${label}] executado`);
        return { autoAdvance: true, handle: "a" };
    }
  }, [engine, addMsg]);

  // ─── Execute one step ───
  const executeStep = useCallback(() => {
    const node = engine.getCurrentNode();
    if (!node) {
      setSimStatus("finished");
      addMsg("system", "Fluxo encerrado");
      return;
    }

    const result = processNode(node);
    if (!result) return;

    updateCanvasHighlights(engine);
    focusNode(engine.currentNodeId);

    if (result.terminal) {
      setSimStatus("finished");
      return;
    }

    if (!result.autoAdvance) {
      setSimStatus("waiting");
      return;
    }

    // Auto-advance to next
    const nextNode = engine.advance(result.handle);
    if (!nextNode) {
      setSimStatus("finished");
      addMsg("system", "Fluxo encerrado (sem conexao de saida)");
      updateCanvasHighlights(engine);
      return;
    }

    setCurrentNodeId(nextNode.id);
    updateCanvasHighlights(engine);
    focusNode(nextNode.id);
    setSimStatus("running");
  }, [engine, processNode, updateCanvasHighlights, focusNode, addMsg]);

  // ─── Start simulation ───
  const handleStart = useCallback(() => {
    engine.reset(nodes, edges);
    setMessages([]);
    setVariables({});
    setWaitingFor(null);
    setInputValue("");

    const startNode = engine.start();
    if (!startNode) {
      addMsg("system", "Nenhum node 'Inicio' encontrado no fluxo.");
      setSimStatus("error");
      return;
    }

    setCurrentNodeId(startNode.id);
    setSimStatus("running");
    updateCanvasHighlights(engine);
    focusNode(startNode.id);

    // Process start node immediately
    const result = processNode(startNode);
    if (result?.autoAdvance) {
      const nextNode = engine.advance(result.handle);
      if (nextNode) {
        setCurrentNodeId(nextNode.id);
        updateCanvasHighlights(engine);
        focusNode(nextNode.id);
      } else {
        setSimStatus("finished");
        addMsg("system", "Fluxo encerrado");
      }
    }
  }, [nodes, edges, engine, addMsg, processNode, updateCanvasHighlights, focusNode]);

  // ─── Step forward ───
  const handleStep = useCallback(() => {
    if (simStatus === "waiting") return; // must answer first
    executeStep();
  }, [simStatus, executeStep]);

  // ─── Reset ───
  const handleReset = useCallback(() => {
    setAutoPlay(false);
    setSimStatus("idle");
    setMessages([]);
    setVariables({});
    setWaitingFor(null);
    setCurrentNodeId(null);
    engine.reset(nodes, edges);
    // Clear canvas highlights
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, _simStatus: null },
      }))
    );
  }, [engine, nodes, edges, setNodes]);

  // ─── User selects an option (menu/button/list) ───
  const handleSelectOption = useCallback((option) => {
    addMsg("user", option.label);
    setWaitingFor(null);

    const nextNode = engine.advance(option.handle);
    if (!nextNode) {
      setSimStatus("finished");
      addMsg("system", "Fluxo encerrado (sem conexao)");
      updateCanvasHighlights(engine);
      return;
    }

    setCurrentNodeId(nextNode.id);
    setSimStatus("running");
    updateCanvasHighlights(engine);
    focusNode(nextNode.id);
  }, [engine, addMsg, updateCanvasHighlights, focusNode]);

  // ─── User sends text input ───
  const handleSendInput = useCallback(() => {
    if (!inputValue.trim() || !waitingFor) return;

    const text = inputValue.trim();
    addMsg("user", text);
    setInputValue("");

    if (waitingFor.varName) {
      engine.setVar(waitingFor.varName, text);
      setVariables({ ...engine.variables });
    }

    setWaitingFor(null);
    const nextNode = engine.advance("a");
    if (!nextNode) {
      setSimStatus("finished");
      addMsg("system", "Fluxo encerrado");
      updateCanvasHighlights(engine);
      return;
    }

    setCurrentNodeId(nextNode.id);
    setSimStatus("running");
    updateCanvasHighlights(engine);
    focusNode(nextNode.id);
  }, [inputValue, waitingFor, engine, addMsg, updateCanvasHighlights, focusNode]);

  // ─── Auto-play effect ───
  useEffect(() => {
    if (!autoPlay || simStatus !== "running") return;
    const timer = setInterval(() => {
      if (!autoPlayRef.current) return;
      executeStep();
    }, speed);
    return () => clearInterval(timer);
  }, [autoPlay, speed, simStatus, executeStep]);

  // ─── Cleanup on close ───
  useEffect(() => {
    if (!open) {
      setAutoPlay(false);
      // Clear sim highlights from canvas
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: { ...n.data, _simStatus: null },
        }))
      );
    }
  }, [open, setNodes]);

  // ─── Key handler for input ───
  const handleInputKey = useCallback((e) => {
    if (e.key === "Enter") handleSendInput();
  }, [handleSendInput]);

  // Stop event propagation to prevent ReactFlow interference
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const currentNode = useMemo(() => engine.getNode(currentNodeId), [engine, currentNodeId]);
  const speedLabel = speed === 2000 ? "0.5x" : speed === 1000 ? "1x" : "2x";

  return (
    <div
      className={`${classes.root} ${open ? classes.visible : classes.hidden}`}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {/* Header */}
      <div className={classes.header}>
        <PlayArrow style={{ fontSize: 18, color: ACCENT }} />
        <Typography className={classes.headerTitle}>
          Simulador de Fluxo
        </Typography>

        <div className={classes.controls}>
          {simStatus === "idle" ? (
            <Tooltip title="Iniciar simulacao">
              <IconButton className={classes.controlBtn} onClick={handleStart} size="small">
                <PlayArrow style={{ fontSize: 18, color: GREEN }} />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Tooltip title={autoPlay ? "Pausar" : "Auto-play"}>
                <IconButton className={classes.controlBtn}
                  onClick={() => setAutoPlay((p) => !p)}
                  disabled={simStatus === "finished" || simStatus === "waiting"}
                  size="small">
                  {autoPlay ? <Pause style={{ fontSize: 18 }} /> : <PlayArrow style={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Proximo passo">
                <span>
                  <IconButton className={classes.controlBtn}
                    onClick={handleStep}
                    disabled={simStatus === "finished" || simStatus === "waiting"}
                    size="small">
                    <SkipNext style={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Reiniciar">
                <IconButton className={classes.controlBtn} onClick={handleReset} size="small">
                  <Replay style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title="Velocidade">
            <span className={classes.speedChip} onClick={cycleSpeed}>
              {speedLabel}
            </span>
          </Tooltip>
        </div>

        <IconButton size="small" onClick={() => { handleReset(); onClose(); }}>
          <CloseIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>

      {/* Body */}
      <div className={classes.body}>
        {/* Chat area */}
        <div className={classes.chatArea}>
          <div className={classes.chatMessages} ref={chatRef}>
            {messages.length === 0 && (
              <Typography variant="body2" style={{ color: "#ccc", textAlign: "center", marginTop: 40 }}>
                Clique em ▶ para iniciar a simulacao
              </Typography>
            )}
            {messages.map((msg, i) => (
              <React.Fragment key={i}>
                <div className={
                  msg.type === "bot" ? classes.msgBot :
                  msg.type === "user" ? classes.msgUser :
                  classes.msgSystem
                }>
                  {msg.text}
                </div>
                {/* Show options after bot message if waiting */}
                {msg.options && i === messages.length - 1 && waitingFor && (
                  <div className={classes.optionBtns}>
                    {/* Rendered outside, see below */}
                  </div>
                )}
              </React.Fragment>
            ))}
            {/* Option buttons */}
            {waitingFor && (waitingFor.type === "menu" || waitingFor.type === "button" || waitingFor.type === "list") && (
              <div className={classes.optionBtns}>
                {waitingFor.options.map((opt) => (
                  <button
                    key={opt.number}
                    className={classes.optionBtn}
                    onClick={() => handleSelectOption(opt)}
                  >
                    {opt.number}. {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input area */}
          <div className={classes.chatInputArea}>
            <TextField
              className={classes.chatInput}
              variant="outlined"
              size="small"
              placeholder={
                waitingFor?.type === "input"
                  ? "Digite sua resposta..."
                  : simStatus === "waiting"
                    ? "Selecione uma opcao acima"
                    : "Simulador em execucao..."
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKey}
              disabled={!waitingFor || waitingFor.type !== "input"}
              fullWidth
            />
            <Tooltip title="Enviar">
              <span>
                <IconButton
                  className={classes.sendBtn}
                  onClick={handleSendInput}
                  disabled={!waitingFor || waitingFor.type !== "input" || !inputValue.trim()}
                  size="small"
                >
                  <Send style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Variables panel */}
        <div className={classes.varsPanel}>
          <div className={classes.varsPanelHeader}>
            Variaveis ({Object.keys(variables).length})
          </div>
          <div className={classes.varsList}>
            {Object.keys(variables).length === 0 ? (
              <Typography variant="caption" style={{ color: "#ccc" }}>
                Nenhuma variavel definida
              </Typography>
            ) : (
              Object.entries(variables).map(([k, v]) => (
                <div key={k} className={classes.varRow}>
                  <span className={classes.varKey}>{k}</span>
                  <span className={classes.varValue} title={String(v)}>{String(v)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className={classes.statusBar}>
        <span
          className={classes.statusDot}
          style={{
            backgroundColor:
              simStatus === "running" ? GREEN :
              simStatus === "waiting" ? AMBER :
              simStatus === "finished" ? GRAY :
              simStatus === "error" ? RED : "#ddd",
          }}
        />
        <span>
          {simStatus === "idle" && "Pronto para simular"}
          {simStatus === "running" && (
            <>Node atual: <span className={classes.nodeLabel}>{getNodeLabel(currentNode)}</span> — Passo {engine.stepCount}</>
          )}
          {simStatus === "waiting" && (
            <>Aguardando entrada do usuario — <span className={classes.nodeLabel}>{getNodeLabel(currentNode)}</span></>
          )}
          {simStatus === "finished" && `Simulacao finalizada — ${engine.stepCount} passos`}
          {simStatus === "error" && "Erro na simulacao"}
        </span>
      </div>
    </div>
  );
}
